import { useEffect, useRef, useState } from 'react';
import { Canvas as FabricCanvas, Rect, Textbox, FabricObject, Line as FabricLine, Path as FabricPath, Ellipse } from 'fabric';
import { useCanvasStore } from '../stores/canvasStore';
import { useStudioStore } from '../stores/studioStore';
import { useViewportStore } from '../stores/viewportStore';
import { useEditorPrefsStore } from '../stores/editorPrefsStore';
import { attachArtboardOverlay, isOverlaySuppressed } from '../components/editor/canvas/ArtboardOverlay';
import { computeWheelNav } from '../components/editor/canvas/wheelNavigation';
import { anchorsToPathData, previewPathData } from '../lib/pathUtils';
import { createPenState, penDrag, penMouseUp, isNearFirstAnchor } from '../lib/penToolState';
import type { PenDrawingState } from '../lib/penToolState';
import { createDefaultLine } from '../lib/elementDefaults';
import { getDefaultButtonFont } from '../lib/defaults';
import { loadCustomFont, ensureVariantLoaded } from '../lib/fontLoader';
import { applyButtonAdvanceFit, ELEMENT_ID_KEY } from '../lib/textMeasurement';
import type { ButtonElement, ShapeElement } from '../types';

interface DrawState {
  isDrawing: boolean;
  tool: string;
  shapeType?: string;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

let drawingPreviewObj: FabricObject | null = null;

function updateDrawingPreview(canvas: FabricCanvas, ds: DrawState, artboardLeft: number, artboardTop: number) {
  if (drawingPreviewObj) {
    canvas.remove(drawingPreviewObj);
    drawingPreviewObj = null;
  }

  const x = Math.min(ds.startX, ds.currentX);
  const y = Math.min(ds.startY, ds.currentY);
  const w = Math.abs(ds.currentX - ds.startX);
  const h = Math.abs(ds.currentY - ds.startY);

  if (w < 2 && h < 2) return;

  let preview: FabricObject | null = null;

  const isEllipse = ds.shapeType === 'circle';

  if (isEllipse) {
    preview = new Ellipse({
      left: artboardLeft + x,
      top: artboardTop + y,
      rx: w / 2,
      ry: h / 2,
      fill: 'rgba(6, 182, 212, 0.1)',
      stroke: '#06b6d4',
      strokeWidth: 1,
      strokeDashArray: [5, 5],
      selectable: false,
      evented: false,
    });
  } else {
    preview = new Rect({
      left: artboardLeft + x,
      top: artboardTop + y,
      width: w,
      height: h,
      fill: 'rgba(6, 182, 212, 0.1)',
      stroke: '#06b6d4',
      strokeWidth: 1,
      strokeDashArray: [5, 5],
      selectable: false,
      evented: false,
    });
  }

  if (preview) {
    (preview as any)._csIsPreview = true;
    canvas.add(preview);
    canvas.requestRenderAll();
    drawingPreviewObj = preview;
  }
}

function clearDrawingPreview(canvas: FabricCanvas | null) {
  if (!canvas || !drawingPreviewObj) return;
  canvas.remove(drawingPreviewObj);
  drawingPreviewObj = null;
  canvas.requestRenderAll();
}

const ARTBOARD_PADDING = 60;
const ARTBOARD_SHADOW_COLOR = 'rgba(0, 0, 0, 0.5)';
const SELECTION_COLOR = 'rgba(0, 188, 212, 0.15)';
const SELECTION_BORDER_COLOR = 'rgba(0, 188, 212, 0.6)';
const CORNER_COLOR = '#06b6d4';
const CORNER_STROKE_COLOR = '#0891b2';

export function useCanvasInit(opts: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  canvasElRef: React.RefObject<HTMLCanvasElement | null>;
}) {
  const { containerRef, canvasElRef } = opts;
  const canvasRef = canvasElRef;

  const artboardRef = useRef<Rect | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  const lineDrawRef = useRef<{ startX: number; startY: number; tempLine: FabricLine | null; isDrawing: boolean }>({
    startX: 0,
    startY: 0,
    tempLine: null,
    isDrawing: false,
  });
  const penStateRef = useRef<PenDrawingState>(createPenState());
  const penPreviewPathRef = useRef<FabricPath | null>(null);
  const penPreviewDotsRef = useRef<FabricObject[]>([]);
  const drawStateRef = useRef<DrawState>({
    isDrawing: false,
    tool: '',
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
  });

  const { setFabricCanvas, canvasWidth, canvasHeight, fabricCanvas } = useCanvasStore();

  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (!canvasRef.current || isInitializedRef.current) return;

    const canvas = new FabricCanvas(canvasRef.current, {
      backgroundColor: '#0a0a0a',
      selection: true,
      selectionColor: SELECTION_COLOR,
      selectionBorderColor: SELECTION_BORDER_COLOR,
      selectionLineWidth: 1,
      preserveObjectStacking: true,
      stopContextMenu: true,
      fireRightClick: true,
    });

    FabricObject.prototype.set({
      transparentCorners: false,
      cornerColor: CORNER_COLOR,
      cornerStrokeColor: CORNER_STROKE_COLOR,
      cornerSize: 8,
      cornerStyle: 'rect',
      borderColor: SELECTION_BORDER_COLOR,
      borderScaleFactor: 1.5,
      padding: 4,
    });

    const artboard = new Rect({
      left: ARTBOARD_PADDING,
      top: ARTBOARD_PADDING,
      width: canvasWidth,
      height: canvasHeight,
      fill: '#ffffff',
      selectable: false,
      evented: false,
      hoverCursor: 'default',
      shadow: {
        color: ARTBOARD_SHADOW_COLOR,
        blur: 20,
        offsetX: 0,
        offsetY: 4,
      } as any,
    });
    (artboard as any)._csIsArtboard = true;
    canvas.add(artboard);
    artboardRef.current = artboard;
    useCanvasStore.getState().setArtboardRef(artboardRef.current);

    const outerBorder = new Rect({
      left: ARTBOARD_PADDING - 1,
      top: ARTBOARD_PADDING - 1,
      width: canvasWidth + 2,
      height: canvasHeight + 2,
      fill: 'transparent',
      stroke: 'rgba(0, 0, 0, 0)',
      strokeWidth: 1,
      selectable: false,
      evented: false,
      hoverCursor: 'default',
      excludeFromExport: true,
      visible: false,
    });
    (outerBorder as any)._csIsArtboardBorder = true;

    const innerBorder = new Rect({
      left: ARTBOARD_PADDING,
      top: ARTBOARD_PADDING,
      width: canvasWidth,
      height: canvasHeight,
      fill: 'transparent',
      stroke: 'rgba(255, 255, 255, 0)',
      strokeWidth: 1,
      selectable: false,
      evented: false,
      hoverCursor: 'default',
      excludeFromExport: true,
      visible: false,
    });
    (innerBorder as any)._csIsArtboardBorder = true;

    canvas.add(outerBorder);
    canvas.add(innerBorder);

    const resizeCanvas = () => {
      if (!containerRef.current) return;
      const { clientWidth, clientHeight } = containerRef.current;
      canvas.setDimensions({ width: clientWidth, height: clientHeight });
      setContainerWidth(clientWidth);
      setContainerHeight(clientHeight);
      canvas.requestRenderAll();
    };
    resizeCanvas();

    const resizeObserver = new ResizeObserver(() => resizeCanvas());
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    setFabricCanvas(canvas);
    isInitializedRef.current = true;

    void useEditorPrefsStore.getState().ensureLoaded();
    const detachArtboardOverlay = attachArtboardOverlay(canvas, {
      getPrefs: () => useEditorPrefsStore.getState().prefs,
      getRect: () => {
        const ab = artboardRef.current;
        if (!ab) return null;
        const width = Number(ab.width ?? 0);
        const height = Number(ab.height ?? 0);
        if (!(width > 0) || !(height > 0)) return null;
        return {
          left: Number(ab.left ?? 0),
          top: Number(ab.top ?? 0),
          width,
          height,
        };
      },
      isSuppressed: () => isOverlaySuppressed(),
    });
    (canvas as any)._csDetachArtboardOverlay = detachArtboardOverlay;

    const unsubPrefs = useEditorPrefsStore.subscribe((state, prev) => {
      if (state.prefs !== prev.prefs) canvas.requestRenderAll();
    });
    (canvas as any)._csUnsubPrefs = unsubPrefs;

    // Custom fonts are initialized in App.tsx via initCustomFonts()

    canvas.on('mouse:wheel', (opt) => {
      const e = opt.e as WheelEvent;
      e.preventDefault();
      e.stopPropagation();

      const vp = useViewportStore.getState();
      const prefs = useEditorPrefsStore.getState().prefs;
      const lowerEl = (canvas as any).lowerCanvasEl as HTMLCanvasElement | undefined;
      const rect = lowerEl?.getBoundingClientRect();
      if (!rect) return;

      const result = computeWheelNav({
        deltaX: e.deltaX,
        deltaY: e.deltaY,
        ctrlKey: e.ctrlKey,
        metaKey: e.metaKey,
        shiftKey: e.shiftKey,
        clientX: e.clientX,
        clientY: e.clientY,
        canvasRectLeft: rect.left,
        canvasRectTop: rect.top,
        currentZoom: vp.zoom,
        panX: vp.panX,
        panY: vp.panY,
        defaultAction: prefs.nav_wheel_action,
        invertScroll: prefs.nav_invert_scroll,
        zoomSpeed: prefs.nav_zoom_speed,
      });

      if (result.kind === 'pan') {
        vp.setPan(result.panX, result.panY);
      } else if (result.kind === 'zoom') {
        vp.setPan(result.panX, result.panY);
        vp.setZoom(result.zoom);
      }
    });

    let spacePressed = false;
    let panStartX = 0;
    let panStartY = 0;
    let panStartPanX = 0;
    let panStartPanY = 0;
    let savedSelection = true;
    let savedObjStates: Array<{ obj: FabricObject; selectable: boolean; evented: boolean }> = [];

    const handlePanKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) {
        const target = e.target as HTMLElement;
        if (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable
        ) return;

        const activeObj = canvas.getActiveObject();
        if (activeObj instanceof Textbox && activeObj.isEditing) return;

        spacePressed = true;
        if (canvas.upperCanvasEl) {
          canvas.upperCanvasEl.style.cursor = 'grab';
        }
        savedSelection = canvas.selection ?? true;
        canvas.selection = false;
        savedObjStates = [];
        canvas.forEachObject((obj) => {
          if (!(obj as any)._csIsArtboard && !(obj as any)._csIsGrid && !(obj as any)._csIsArtboardBorder) {
            savedObjStates.push({ obj, selectable: obj.selectable ?? true, evented: obj.evented ?? true });
            obj.selectable = false;
            obj.evented = false;
          }
        });
      }
    };

    const handlePanKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        spacePressed = false;
        if (canvas.upperCanvasEl) {
          canvas.upperCanvasEl.style.cursor = 'default';
        }
        canvas.selection = savedSelection;
        for (const s of savedObjStates) {
          s.obj.selectable = s.selectable;
          s.obj.evented = s.evented;
        }
        savedObjStates = [];
        useViewportStore.getState().setIsPanning(false);
      }
    };

    document.addEventListener('keydown', handlePanKeyDown);
    document.addEventListener('keyup', handlePanKeyUp);

    canvas.on('mouse:down', (opt) => {
      const e = opt.e as MouseEvent;
      if (spacePressed || e.button === 1) {
        useViewportStore.getState().setIsPanning(true);
        panStartX = e.clientX;
        panStartY = e.clientY;
        const vp = useViewportStore.getState();
        panStartPanX = vp.panX;
        panStartPanY = vp.panY;
        if (canvas.upperCanvasEl) {
          canvas.upperCanvasEl.style.cursor = 'grabbing';
        }
      }
    });

    canvas.on('mouse:move', (opt) => {
      const ds = drawStateRef.current;
      if (ds.isDrawing && artboardRef.current) {
        // Force VPT-inverse via explicit getPointer; opt.pointer is unreliable
        // with custom viewportTransform in Fabric v6.
        const pointer = canvas.getPointer(opt.e);
        if (!pointer) return;
        const abLeft = artboardRef.current.left ?? ARTBOARD_PADDING;
        const abTop = artboardRef.current.top ?? ARTBOARD_PADDING;
        ds.currentX = Math.round(pointer.x - abLeft);
        ds.currentY = Math.round(pointer.y - abTop);

        if ((opt.e as MouseEvent).shiftKey) {
          const size = Math.max(Math.abs(ds.currentX - ds.startX), Math.abs(ds.currentY - ds.startY));
          ds.currentX = ds.startX + (ds.currentX >= ds.startX ? size : -size);
          ds.currentY = ds.startY + (ds.currentY >= ds.startY ? size : -size);
        }

        updateDrawingPreview(canvas, ds, abLeft, abTop);
        return;
      }

      if (lineDrawRef.current.isDrawing && lineDrawRef.current.tempLine) {
        const tempLine = lineDrawRef.current.tempLine;
        // Force VPT-inverse via explicit getPointer.
        const pointer = canvas.getPointer(opt.e);
        if (!pointer) return;
        let x2 = pointer.x;
        let y2 = pointer.y;

        if ((opt.e as MouseEvent).shiftKey) {
          const dx = x2 - (tempLine.x1 ?? 0);
          const dy = y2 - (tempLine.y1 ?? 0);
          const angle = Math.atan2(dy, dx);
          const snappedAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
          const length = Math.sqrt(dx * dx + dy * dy);
          x2 = (tempLine.x1 ?? 0) + length * Math.cos(snappedAngle);
          y2 = (tempLine.y1 ?? 0) + length * Math.sin(snappedAngle);
        }
        tempLine.set({ x2, y2 });
        canvas.requestRenderAll();
        return;
      }

      const penState = penStateRef.current;
      if (penState.isActive && artboardRef.current) {
        // Force VPT-inverse via explicit getPointer.
        const pointer = canvas.getPointer(opt.e);
        if (!pointer) return;
        const abLeft = artboardRef.current.left ?? ARTBOARD_PADDING;
        const abTop = artboardRef.current.top ?? ARTBOARD_PADDING;
        const curX = Math.round(pointer.x - abLeft);
        const curY = Math.round(pointer.y - abTop);

        if (penState.isDragging) {
          penDrag(penState, curX, curY);
        }

        if (penPreviewPathRef.current) {
          canvas.remove(penPreviewPathRef.current);
          penPreviewPathRef.current = null;
        }

        let pathStr: string;
        if (penState.isDragging) {
          pathStr = anchorsToPathData(penState.anchors, false);
        } else {
          pathStr = previewPathData(penState.anchors, curX, curY);
        }

        if (pathStr) {
          try {
            const preview = new FabricPath(pathStr, {
              left: abLeft,
              top: abTop,
              fill: 'transparent',
              stroke: '#06b6d4',
              strokeWidth: 2,
              strokeDashArray: [6, 4],
              selectable: false,
              evented: false,
              objectCaching: false,
            });
            (preview as any)._csPenPreview = true;
            canvas.add(preview);
            penPreviewPathRef.current = preview;
          } catch { /* ignore invalid path */ }
        }
        canvas.requestRenderAll();
        return;
      }

      if (!useViewportStore.getState().isPanning) return;
      const e = opt.e as MouseEvent;
      const dx = e.clientX - panStartX;
      const dy = e.clientY - panStartY;
      useViewportStore.getState().setPan(panStartPanX + dx, panStartPanY + dy);
    });

    canvas.on('mouse:up', (opt) => {
      const ds = drawStateRef.current;
      if (ds.isDrawing) {
        ds.isDrawing = false;
        clearDrawingPreview(canvas);

        const x = Math.min(ds.startX, ds.currentX);
        const y = Math.min(ds.startY, ds.currentY);
        const w = Math.abs(ds.currentX - ds.startX);
        const h = Math.abs(ds.currentY - ds.startY);
        const isClick = w < 5 && h < 5;

        const DEFAULTS: Record<string, { w: number; h: number }> = {
          rectangle: { w: 200, h: 150 },
          ellipse: { w: 200, h: 150 },
          button: { w: 180, h: 48 },
          shape: { w: 150, h: 150 },
        };
        const def = DEFAULTS[ds.tool] || { w: 200, h: 150 };

        const fx = isClick ? ds.startX : x;
        const fy = isClick ? ds.startY : y;
        const fw = isClick ? def.w : Math.max(w, 20);
        const fh = isClick ? def.h : Math.max(h, 20);

        const currentElements = useStudioStore.getState().canvasState.elements;

        if (ds.tool === 'button') {
          const defaultBtnFont = getDefaultButtonFont();
          if (!defaultBtnFont) {
            console.warn('[Button Tool] No custom fonts available in cs_fonts. Upload a font in Font Manager before creating button elements.');
            useCanvasStore.getState().setActiveTool('select');
            return;
          }

          const newElement: ButtonElement = {
            id: crypto.randomUUID(),
            type: 'button',
            name: 'New Button',
            x: Math.round(fx),
            y: Math.round(fy),
            width: Math.round(fw),
            height: Math.round(fh),
            rotation: 0,
            visible: true,
            locked: false,
            zIndex: currentElements.length,
            opacity: 1,
            layer: 'OVERLAY_LAYER',
            contentType: 'STATIC',
            text: 'Click Here',
            fontFamily: defaultBtnFont.fontFamily,
            fontVariantId: defaultBtnFont.fontVariantId,
            fontSize: 16,
            fontWeight: defaultBtnFont.fontWeight,
            fontStyle: defaultBtnFont.fontStyle === 'italic' ? 'italic' : 'normal',
            color: '#ffffff',
            backgroundColor: '#06b6d4',
            borderRadius: 8,
            paddingX: 24,
            paddingY: 12,
          };

          loadCustomFont(defaultBtnFont.fontFamily)
            .then(() => ensureVariantLoaded(
              defaultBtnFont.fontFamily,
              defaultBtnFont.fontWeight,
              defaultBtnFont.fontStyle
            ))
            .then(() => {
              useStudioStore.getState().addElement(newElement);

              // Phase F.2: One-time fit-to-text at creation. If the default
              // button would clip the initial label, grow to fit. After this,
              // button width is fully user-controlled.
              requestAnimationFrame(() => {
                const fc = useCanvasStore.getState().fabricCanvas;
                if (!fc) return;
                const fabricObj = fc.getObjects().find(
                  (o: any) => o[ELEMENT_ID_KEY] === newElement.id
                );
                if (!fabricObj) return;
                const fit = applyButtonAdvanceFit(fabricObj, newElement);
                if (fit.changed) {
                  useStudioStore.getState().updateElement(newElement.id, { width: fit.newWidth } as any);
                }
              });
            })
            .catch((err) => {
              console.error('[Button Tool] Font load failed:', err);
              useCanvasStore.getState().setActiveTool('select');
            });
        } else if (ds.tool === 'shape' && ds.shapeType) {
          const labelName = ds.shapeType.charAt(0).toUpperCase() + ds.shapeType.slice(1).replace('-', ' ');
          const newElement: ShapeElement = {
            id: crypto.randomUUID(),
            type: 'shape',
            name: `New ${labelName}`,
            x: Math.round(fx),
            y: Math.round(fy),
            width: Math.round(fw),
            height: Math.round(fh),
            rotation: 0,
            visible: true,
            locked: false,
            zIndex: currentElements.length,
            opacity: 1,
            layer: 'BACKGROUND_LAYER',
            contentType: 'STATIC',
            shapeType: ds.shapeType as ShapeElement['shapeType'],
            fill: '#06b6d4',
            fillOpacity: 1,
            stroke: 'transparent',
            strokeWidth: 0,
            borderRadius: 0,
          };
          useStudioStore.getState().addElement(newElement);
        }

        useCanvasStore.getState().setActiveTool('select');
        canvas.requestRenderAll();
        return;
      }

      if (penStateRef.current.isDragging) {
        penMouseUp(penStateRef.current);
      }

      if (lineDrawRef.current.isDrawing) {
        const { startX, startY, tempLine } = lineDrawRef.current;
        lineDrawRef.current.isDrawing = false;
        if (tempLine) {
          canvas.remove(tempLine);
          lineDrawRef.current.tempLine = null;
        }

        if (!artboardRef.current) return;
        const upPointer = canvas.getPointer(opt.e);
        if (!upPointer) return;
        const artboardLeft = artboardRef.current.left ?? ARTBOARD_PADDING;
        const artboardTop = artboardRef.current.top ?? ARTBOARD_PADDING;
        let endX = Math.round(upPointer.x - artboardLeft);
        let endY = Math.round(upPointer.y - artboardTop);

        if ((opt.e as MouseEvent).shiftKey) {
          const dx = endX - startX;
          const dy = endY - startY;
          const angle = Math.atan2(dy, dx);
          const snappedAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
          const length = Math.sqrt(dx * dx + dy * dy);
          endX = Math.round(startX + length * Math.cos(snappedAngle));
          endY = Math.round(startY + length * Math.sin(snappedAngle));
        }

        const lineLength = Math.sqrt((endX - startX) ** 2 + (endY - startY) ** 2);
        // On a bare click (no drag), fall back to a short default horizontal
        // line anchored at the click point. Matches shape/text/button tools
        // which also create a default-sized element on click.
        if (lineLength < 5) {
          endX = startX + 80;
          endY = startY;
        }

        const minX = Math.min(startX, endX);
        const minY = Math.min(startY, endY);
        const maxX = Math.max(startX, endX);
        const maxY = Math.max(startY, endY);

        const newLine = createDefaultLine({
          x: minX,
          y: minY,
          width: maxX - minX || 1,
          height: maxY - minY || 1,
          x1: startX - minX,
          y1: startY - minY,
          x2: endX - minX,
          y2: endY - minY,
          zIndex: useStudioStore.getState().canvasState.elements.length,
        });

        useStudioStore.getState().pushUndoState();
        useStudioStore.getState().addElement(newLine);
        useStudioStore.getState().selectElement(newLine.id);
        useCanvasStore.getState().setActiveTool('select');
        return;
      }

      if (useViewportStore.getState().isPanning) {
        useViewportStore.getState().setIsPanning(false);
        if (spacePressed && canvas.upperCanvasEl) {
          canvas.upperCanvasEl.style.cursor = 'grab';
        } else if (canvas.upperCanvasEl) {
          canvas.upperCanvasEl.style.cursor = 'default';
        }
      }
    });

    return () => {
      document.removeEventListener('keydown', handlePanKeyDown);
      document.removeEventListener('keyup', handlePanKeyUp);
      resizeObserver.disconnect();
      try { (canvas as any)._csDetachArtboardOverlay?.(); } catch { /* noop */ }
      try { (canvas as any)._csUnsubPrefs?.(); } catch { /* noop */ }
      canvas.dispose();
      setFabricCanvas(null);
      useCanvasStore.getState().setArtboardRef(null);
      isInitializedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!fabricCanvas) return;
    const { zoom, panX, panY } = useViewportStore.getState();
    const vpt: [number, number, number, number, number, number] = [zoom, 0, 0, zoom, panX, panY];
    fabricCanvas.setViewportTransform(vpt);
    fabricCanvas.requestRenderAll();

    let prevZoom = zoom;
    let prevPanX = panX;
    let prevPanY = panY;
    const unsub = useViewportStore.subscribe((state) => {
      if (state.zoom === prevZoom && state.panX === prevPanX && state.panY === prevPanY) {
        return;
      }
      prevZoom = state.zoom;
      prevPanX = state.panX;
      prevPanY = state.panY;
      const newVpt: [number, number, number, number, number, number] = [state.zoom, 0, 0, state.zoom, state.panX, state.panY];
      fabricCanvas.setViewportTransform(newVpt);
      fabricCanvas.requestRenderAll();
    });

    return unsub;
  }, [fabricCanvas]);

  useEffect(() => {
    if (!fabricCanvas || !containerRef.current) return;
    const container = containerRef.current;
    requestAnimationFrame(() => {
      const rect = container.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        useViewportStore.getState().fitToScreen(canvasWidth, canvasHeight, rect.width, rect.height);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fabricCanvas, canvasWidth, canvasHeight]);

  useEffect(() => {
    if (!fabricCanvas || !artboardRef.current) return;
    artboardRef.current.set({ width: canvasWidth, height: canvasHeight });
    artboardRef.current.setCoords();

    fabricCanvas.getObjects().forEach((obj) => {
      if (!(obj as any)._csIsArtboardBorder) return;
      const isOuter = (obj as any).stroke === 'rgba(0, 0, 0, 0.5)';
      if (isOuter) {
        obj.set({
          left: ARTBOARD_PADDING - 1,
          top: ARTBOARD_PADDING - 1,
          width: canvasWidth + 2,
          height: canvasHeight + 2,
        });
      } else {
        obj.set({
          left: ARTBOARD_PADDING,
          top: ARTBOARD_PADDING,
          width: canvasWidth,
          height: canvasHeight,
        });
      }
      obj.setCoords();
      fabricCanvas.bringObjectToFront(obj);
    });

    fabricCanvas.requestRenderAll();
  }, [fabricCanvas, canvasWidth, canvasHeight]);

  return { fabricCanvas, artboardRef, containerWidth, containerHeight, lineDrawRef, penStateRef, penPreviewPathRef, penPreviewDotsRef, drawStateRef };
}
